'use client';

import Cookies from 'js-cookie';
import React, { useCallback, useEffect, type ReactNode } from 'react';
import { Country, COUNTRY_KEY, CountryCurrency, Currency, CURRENCY_KEY } from '../constants';
import { getCountryCookie, setCountryCookie, setCurrencyCookie } from '../utils';
import { createSafeContext } from './createSafeContext';
const DEFAULT_COUNTRY: Country = Country.Singapore;
const DEFAULT_CURRENCY: Currency = Currency.Singapore;
interface RegionCurrencyContextValue {
  country: Country;
  currency: Currency;
  setCountry: (country: Country) => void;
}
const [RegionCurrencyProvider, useRegionCurrencyContext] = createSafeContext<RegionCurrencyContextValue>('RegionCurrencyContext');
interface RegionCurrencyProviderProps {
  children: ReactNode;
  initialCountry?: Country;
}
export function RegionCurrencyContextProvider({
  children,
  initialCountry
}: RegionCurrencyProviderProps) {
  const initialResolvedCountry = getCountryCookie() ?? initialCountry ?? DEFAULT_COUNTRY;
  const [country, setCountry] = React.useState<Country>(initialResolvedCountry);
  const [currency, setCurrency] = React.useState<Currency>(CountryCurrency[initialResolvedCountry]);
  const handleSetCountry = useCallback((newCountry: Country) => {
    if (newCountry === country) return;
    const newCurrency = CountryCurrency[newCountry];
    setCountry(newCountry);
    setCountryCookie(newCountry);
    setCurrency(newCurrency);
    setCurrencyCookie(newCurrency);
  }, [country]);
  const hasSyncedRef = React.useRef(false);

  /* ------------ Cookie polling configuration ------------- */
  const POLLING_INTERVAL_MS = 300;
  const MAX_POLL_ATTEMPTS = 10;

  /**
   * Try reading currency & country from cookie.
   * If values are found, apply them to state and return true.
   * Otherwise return false so the caller can keep polling.
   */
  const syncStateWithCookie = useCallback((): boolean => {
    const _cookieCountry = getCountryCookie() ?? initialCountry ?? DEFAULT_COUNTRY;
    const cookieCountry = Object.values(Country).includes(_cookieCountry) ? _cookieCountry : DEFAULT_COUNTRY;
    const cookieCurrency = CountryCurrency[cookieCountry];
    setCurrency(prev => prev === cookieCurrency ? prev : cookieCurrency);
    setCountry(prev => prev === cookieCountry ? prev : cookieCountry);
    return true;
  }, [initialCountry]);

  // Poll cookie up to MAX_POLL_ATTEMPTS times (every POLLING_INTERVAL_MS) until it becomes available.
  useEffect(() => {
    if (hasSyncedRef.current) return;

    // First immediate attempt
    if (syncStateWithCookie()) {
      hasSyncedRef.current = true;
      return;
    }
    let attempts = 0;
    const intervalId = setInterval(() => {
      attempts += 1;
      const success = syncStateWithCookie();
      const reachedLimit = attempts >= MAX_POLL_ATTEMPTS;
      if (success || reachedLimit) {
        hasSyncedRef.current = true;
        clearInterval(intervalId);
      }
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [syncStateWithCookie]);
  useEffect(() => {
    if (!hasSyncedRef.current) {
      return;
    }
    Cookies.set(CURRENCY_KEY, currency);
    Cookies.set(COUNTRY_KEY, country);
  }, [currency, country]);
  return <RegionCurrencyProvider value={{
    country,
    currency,
    setCountry: handleSetCountry
  }} data-sentry-element="RegionCurrencyProvider" data-sentry-component="RegionCurrencyContextProvider" data-sentry-source-file="regionCurrencyContext.tsx">
      {children}
    </RegionCurrencyProvider>;
}
export const useRegionCurrency = useRegionCurrencyContext;